import {
  Component, ChangeDetectionStrategy,
  signal, computed, input, AfterViewInit, OnDestroy, inject, NgZone, DestroyRef
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { QuoteCtaDirective } from '../../../../core/directives/quote-cta.directive';

// MV-298: when scrollY is within this many pixels of the top, force-hide the
// bar so it never overlaps the navbar+breadcrumb reading zone. Robust against
// iOS scroll mechanics that can leave `scrollingDown` stuck at true.
const NEAR_TOP_THRESHOLD_PX = 200;

@Component({
  selector: 'app-announcement-bar',
  standalone: true,
  imports: [QuoteCtaDirective],
  templateUrl: './announcement-bar.component.html',
  styleUrl: './announcement-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnnouncementBarComponent implements AfterViewInit, OnDestroy {
  private zone       = inject(NgZone);
  private router     = inject(Router);
  private destroyRef = inject(DestroyRef);

  /** true when user is scrolling down */
  readonly scrollingDown = signal(false);

  /** true when the contact form is visible on screen */
  readonly formVisible = signal(false);

  /** MV-298: true when scrollY is within NEAR_TOP_THRESHOLD_PX of viewport top.
   *  Force-hides the bar so it never overlaps navbar+breadcrumb. */
  readonly nearTop = signal(true);

  /** Story / test override — bypasses all hide logic when `true`. */
  readonly forceVisible = input<boolean>(false);

  /** false until after first paint — prevents FOUC by deferring CSS transitions */
  readonly animated = signal(false);

  /** Bar shows when scrolling down, form not visible, and not within
   *  NEAR_TOP_THRESHOLD_PX of viewport top. */
  readonly visible = computed(() =>
    this.animated() && (this.forceVisible() ||
      (this.scrollingDown() && !this.formVisible() && !this.nearTop()))
  );

  private scrollHandler: (() => void) | null = null;
  private lastScrollY = 0;
  private formEl: Element | null = null;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.zone.run(() => this.animated.set(true));
      });
      this.findFormAndListen();
    });

    // Re-find form after each route change
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      setTimeout(() => {
        this.zone.runOutsideAngular(() => this.findFormAndListen());
      }, 100);
    });
  }

  ngOnDestroy(): void {
    this.removeScrollHandler();
  }

  private findFormAndListen(): void {
    this.removeScrollHandler();
    this.lastScrollY = window.scrollY;

    // Reset signals — page just navigated, scrollY is back near 0
    this.zone.run(() => {
      this.scrollingDown.set(false);
      this.formVisible.set(false);
      this.nearTop.set(this.lastScrollY < NEAR_TOP_THRESHOLD_PX);
    });

    this.formEl = document.querySelector('#get-a-call');
    this.setupScrollHandler();
  }

  private setupScrollHandler(): void {
    this.scrollHandler = () => {
      const currentY = window.scrollY;

      // Scroll direction (with 5px deadzone to avoid flicker)
      if (Math.abs(currentY - this.lastScrollY) > 5) {
        const isDown = currentY > this.lastScrollY;
        if (isDown !== this.scrollingDown()) {
          this.zone.run(() => this.scrollingDown.set(isDown));
        }
        this.lastScrollY = currentY;
      }

      // MV-298: near-top check fires every event (no deadzone) so the bar
      // hides reliably even when scroll-direction tracking gets stuck on iOS.
      const isNearTop = currentY < NEAR_TOP_THRESHOLD_PX;
      if (isNearTop !== this.nearTop()) {
        this.zone.run(() => this.nearTop.set(isNearTop));
      }

      // Form visibility check
      const formRect = this.formEl?.getBoundingClientRect();
      const isFormVisible = !!formRect && formRect.top < window.innerHeight && formRect.bottom > 0;
      if (isFormVisible !== this.formVisible()) {
        this.zone.run(() => this.formVisible.set(isFormVisible));
      }
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  private removeScrollHandler(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }
}
